import React, { useEffect, useState } from "react";
import { ArrowRight, TrendingUp, Sparkles, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import RadarGame from "@/components/RadarGame";
import { getStats } from "@/lib/api";

const highlights = [
  { icon: TrendingUp, label: "Live search & trend signals" },
  { icon: Sparkles, label: "AI-detected opportunities" },
  { icon: Globe, label: "News & global media coverage" },
];

export default function Hero({ onCtaClick }) {
  const [sweeps, setSweeps] = useState(null);

  useEffect(() => {
    getStats().then((s) => setSweeps(s.sweeps)).catch(() => {});
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background radar glow */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] ds-glow-radial pointer-events-none"
      />

      {/* Coordinate strip */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-10 flex items-center justify-between text-[11px] tracking-[0.3em] uppercase text-neutral-500">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[#FF5000] ds-pulse" />
          <span data-testid="hero-status">Signal · Live</span>
        </div>
        <div className="hidden md:block ds-mono">
          {sweeps ? (
            <span data-testid="hero-sweep-count">
              {sweeps.toLocaleString()} sweeps run · Free · No sign-in
            </span>
          ) : (
            "Free · No sign-in · Market intel system"
          )}
        </div>
        <div className="ds-mono">v0.1 · Beta</div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 items-center">
        {/* Headline */}
        <div className="col-span-12 lg:col-span-8 ds-fade-up">
          <p className="text-[12px] tracking-[0.32em] uppercase text-[#FF5000] mb-6">
            Market intelligence · Public signal layer
          </p>
          <h1
            data-testid="hero-headline"
            className="text-5xl sm:text-6xl md:text-7xl lg:text-[80px] font-semibold tracking-[-0.035em] leading-[0.98] text-white"
          >
            Market Intel{" "}
            <span className="relative inline-block">
              <span className="relative z-10">System</span>
              <span
                aria-hidden
                className="absolute -bottom-1 left-0 right-0 h-[10px] bg-[#FF5000]/30"
              />
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 max-w-2xl text-lg md:text-xl text-neutral-400 leading-relaxed"
          >
            Type a location and your inquiry. DataSniper collects live signals
            from Google Trends, Google News and GDELT, then runs an AI analysis
            relating to your inquiry with the data pulled... Trends.. Demands..
            Opportunities... Oh and it's free!
          </p>

          {/* Quick value row */}
          <div
            data-testid="hero-highlights"
            className="mt-7 flex flex-wrap gap-x-8 gap-y-3"
          >
            {highlights.map((h) => (
              <span
                key={h.label}
                className="flex items-center gap-2.5 text-[13px] md:text-[14px] text-neutral-300"
              >
                <h.icon className="w-4 h-4 text-[#FF5000]" />
                {h.label}
              </span>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Link
              to="/report"
              data-testid="hero-datasnipe-btn"
              data-cta="true"
              className="group inline-flex items-center gap-3 bg-[#FF5000] hover:bg-[#FF7333] text-white text-[15px] font-medium px-8 py-4 transition-all duration-200"
            >
              DataSnipe
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>

            <button
              data-testid="hero-cta-btn"
              onClick={onCtaClick}
              className="group inline-flex items-center gap-3 border border-neutral-700 hover:border-[#FF5000] text-white text-[15px] font-medium px-7 py-4 transition-all duration-200"
            >
              Join the Community
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Right rail: interactive radar mini-game */}
        <div className="hidden lg:flex col-span-4 items-center justify-end ds-fade-up">
          <RadarGame />
        </div>
      </div>
    </section>
  );
}
